import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
  Field,
  FieldError,
  FieldLabel,
} from '@monksflow/monks-ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonaTagFieldProps } from './PersonaFields.types';

/**
 * Multi-value field: chips for what is chosen, a list for what is on offer, and
 * — since every one of these accepts values the workspace has not used before —
 * the typed text itself as the last option.
 */
export const PersonaTagField = ({
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  disabled,
  options,
}: PersonaTagFieldProps) => {
  const { t } = useTranslation('audiences');
  const id = useId();
  const [query, setQuery] = useState('');
  // The popup anchors to the chips box, not to the bare input inside it.
  const anchor = useComboboxAnchor();

  const trimmedQuery = query.trim();
  // Every selected value has to stay in the collection: a controlled Combobox
  // whose value is not among its items cannot resolve the selection, and
  // picking an option silently does nothing.
  const known = [...new Set([...options, ...value])];
  const canAdd = trimmedQuery.length > 0 && !known.includes(trimmedQuery);
  const items = canAdd ? [...known, trimmedQuery] : known;

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </FieldLabel>
      <Combobox
        multiple
        items={items}
        value={value}
        onValueChange={(next: string[]) => {
          onChange(next);
          setQuery('');
        }}
        onInputValueChange={setQuery}
        disabled={disabled}
      >
        <ComboboxChips ref={anchor} aria-invalid={Boolean(error) || undefined}>
          {value.map((item) => (
            <ComboboxChip key={item}>{item}</ComboboxChip>
          ))}
          <ComboboxChipsInput id={id} placeholder={placeholder} />
        </ComboboxChips>
        {/* Radix's modal Sheet sets `pointer-events: none` on the body, and this
            popup portals outside the dialog, so it inherits it and every option
            renders but refuses the mouse. Re-enabling pointer events on the popup
            itself is what makes the list usable inside a sheet. */}
        <ComboboxContent anchor={anchor} className="pointer-events-auto">
          <ComboboxList>
            <ComboboxCollection>
              {(item: string) => (
                <ComboboxItem key={item} value={item}>
                  {item === trimmedQuery && canAdd
                    ? t('audiences.personaTagField.addValue', { value: item })
                    : item}
                </ComboboxItem>
              )}
            </ComboboxCollection>
          </ComboboxList>
          <ComboboxEmpty>{t('audiences.personaTagField.noOptions')}</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

PersonaTagField.displayName = 'PersonaTagField';
